// Chakra imports
import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    Select,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Tfoot,
    Th,
    Thead, toast,
    Tr,
    useColorModeValue,
    useDisclosure, useToast,
} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import React, {useCallback, useEffect, useState} from "react";
import {FiChevronLeft, FiChevronRight, FiSearch} from "react-icons/fi";
import SchoolTableRow from "./SchoolTableRow";
import AddSchoolModal from "./AddSchoolModal";
import {fetchSchoolDetail, saveSchool, searchSchool, updateSchool} from "./Services";
import SchoolProfile from "../SchoolProfile";

// Keep the admin on the same school detail view (and tab) across a page
// refresh by persisting the selection to sessionStorage.
const SS_SELECTED_SCHOOL = "admin:schoolSetup:selectedSchool";
const SS_SHOW_DETAIL = "admin:schoolSetup:showDetail";
const SS_ACTIVE_TAB = "admin:schoolProfile:activeTab";

// Interactive search filters for the school list. Status codes mirror the
// badge logic in SchoolTableRow (Y = Active, N = Inactive, B = Blocked).
const INITIAL_FILTERS = {name: "", code: "", status: ""};

// Build the list of page tokens to render as numbered buttons. Always shows
// the first and last page; collapses long gaps around the current page into
// "…" so the control stays compact (e.g. 1 … 4 5 6 … 20).
const buildPageItems = (current, total) => {
    if (total <= 7) {
        return Array.from({length: total}, (_, i) => i + 1);
    }
    const items = [1];
    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);
    if (left > 2) items.push("left-ellipsis");
    for (let p = left; p <= right; p += 1) items.push(p);
    if (right < total - 1) items.push("right-ellipsis");
    items.push(total);
    return items;
};

const School = ({title, captions, data}) => {

    const toast = useToast();

    const textColor = useColorModeValue("gray.700", "white");

    const {isOpen, onOpen, onClose} = useDisclosure(); // Add modal state management

    // Edit modal state — separate from the add flow so both can coexist.
    const {isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose} = useDisclosure();
    const [editSchoolData, setEditSchoolData] = useState(null);
    const [isLoadingEdit, setIsLoadingEdit] = useState(false);

    const [schoolList, setSchoolList] = useState("");
    const [isLoadingSchools, setIsLoadingSchools] = useState(false);

    // Search + pagination state (page is 1-based to match the search API).
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const storedSchool = sessionStorage.getItem(SS_SELECTED_SCHOOL);
    const [showDetail, setShowDetail] = useState(
        sessionStorage.getItem(SS_SHOW_DETAIL) === "true" && !!storedSchool
    );
    const [selectedSchool, setSelectedSchool] = useState(storedSchool || {});

    // Persist / clear the detail selection so a refresh restores it.
    useEffect(() => {
        if (showDetail && selectedSchool) {
            sessionStorage.setItem(SS_SHOW_DETAIL, "true");
            sessionStorage.setItem(SS_SELECTED_SCHOOL, String(selectedSchool));
        } else {
            sessionStorage.removeItem(SS_SHOW_DETAIL);
            sessionStorage.removeItem(SS_SELECTED_SCHOOL);
            // Leaving the detail view resets the tab so the next school opens on
            // OVERVIEW (a refresh, which never clears these, keeps the tab).
            sessionStorage.removeItem(SS_ACTIVE_TAB);
        }
    }, [showDetail, selectedSchool]);

    // Navigating to another page/menu item unmounts this page (a refresh does a
    // full reload and never runs this cleanup). Clear the saved selection so the
    // School Setup page shows the school list again on return.
    useEffect(() => {
        return () => {
            sessionStorage.removeItem(SS_SHOW_DETAIL);
            sessionStorage.removeItem(SS_SELECTED_SCHOOL);
            sessionStorage.removeItem(SS_ACTIVE_TAB);
        };
    }, []);

    const setShowSchoolDetail = (id, flag) => {
        setShowDetail(flag);
        setSelectedSchool(id);
    }

    const fetchSchoolList = useCallback(async (activeFilters, targetPage, targetSize) => {
        setIsLoadingSchools(true);

        try {
            let request = {
                name: activeFilters.name || "",
                code: activeFilters.code || "",
                status: activeFilters.status || "",
            }
            // The UI `page` is 1-based, but the backend search API is zero-based
            // (Spring PageRequest), so the first page must be sent as page=0.
            const response = await searchSchool(request, targetPage - 1, targetSize);
            if (response.status === 200) {
                const {totalItems, schoolList} = response.data
                setSchoolList(schoolList || []);
                setTotalItems(totalItems || 0);
                setTotalPages(Math.max(1, Math.ceil((totalItems || 0) / targetSize)));
            }
        } catch (e) {
            console.error("Error while fetching school list e:", e);
        } finally {
            setIsLoadingSchools(false);
        }
    }, []);

    // Interactive search: debounce filter/page/size changes into one API call so
    // typing in the Name/Code fields fetches live without spamming the backend.
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchSchoolList(filters, page, size);
        }, 350);
        return () => clearTimeout(timer);
    }, [filters, page, size, fetchSchoolList]);

    // Changing any filter resets to the first page so results start from the top.
    const handleFilterChange = (event) => {
        const {name, value} = event.target;
        setFilters((current) => ({...current, [name]: value}));
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const handleSchoolSubmit = async (schoolDetails) => {

        let request = {
            name: schoolDetails.name,
            address: schoolDetails.address,
            email: schoolDetails.email,
            phone: schoolDetails.phone,
            website: schoolDetails.website,
            type: schoolDetails.type,
            subType: schoolDetails.subtype,
            totalStudent: schoolDetails.students,
            totalClass: schoolDetails.classes,
            speakersNeeded: schoolDetails.speakersNeeded,
            description: schoolDetails.description,
            logo: schoolDetails.logo,
            banner: schoolDetails.banner,
            maxBell: schoolDetails.maxBells,
            profileMenuCodes: schoolDetails.profileMenuCodes,
        }

        try {
            const response = await saveSchool(request);
            console.log(response)
            console.log(response)
            if (response.status === 201) {
                toast({
                    title: "Success!",
                    description: "School details saved successfully.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                onClose();
                fetchSchoolList(filters, page, size);
            }

        } catch (e) {
            console.error("Error while submiting school details e:", e);
        }
    }

    const handeSchoolUpload=()=>{

    }

    // Edit flow: fetch the full detail by id, then open the prefilled modal.
    const openEditSchool = async (id) => {
        if (!id) return;
        setIsLoadingEdit(true);
        onEditOpen();
        try {
            const response = await fetchSchoolDetail(id);
            if (response.status === 200) {
                setEditSchoolData(response.data);
            }
        } catch (e) {
            console.error("Error while fetching school detail e:", e);
            toast({
                title: "Error!",
                description: "Failed to load school details. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            onEditClose();
        } finally {
            setIsLoadingEdit(false);
        }
    }

    const closeEditSchool = () => {
        onEditClose();
        setEditSchoolData(null);
    }

    const handleSchoolUpdate = async (schoolDetails) => {
        if (!editSchoolData?.id) return;

        let request = {
            name: schoolDetails.name,
            address: schoolDetails.address,
            email: schoolDetails.email,
            phone: schoolDetails.phone,
            website: schoolDetails.website,
            type: schoolDetails.type,
            subType: schoolDetails.subtype,
            totalStudent: schoolDetails.students,
            totalClass: schoolDetails.classes,
            speakersNeeded: schoolDetails.speakersNeeded,
            description: schoolDetails.description,
            logo: schoolDetails.logo,
            banner: schoolDetails.banner,
            maxBell: schoolDetails.maxBells,
            profileMenuCodes: schoolDetails.profileMenuCodes,
        }

        try {
            const response = await updateSchool(editSchoolData.id, request);
            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Success!",
                    description: "School details updated successfully.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                closeEditSchool();
                fetchSchoolList(filters, page, size);
            }
        } catch (e) {
            console.error("Error while updating school details e:", e);
            toast({
                title: "Error!",
                description: "Failed to update school details. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    return (
        <Card overflowX={{sm: "scroll", xl: "hidden"}}>
            {showDetail?
                <SchoolProfile selectedSchool={selectedSchool} setShowSchoolDetail={setShowDetail}/> :
                <>
                    <CardHeader p="16px 0 24px 0">
                        <Flex direction="column" width="100%" gap="16px">
                            <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                                {/* Title on the left */}
                                <Text fontSize="lg" color={textColor} fontWeight="semibold" flex="1" isTruncated>
                                    {title}
                                </Text>

                                {/* Buttons on the right */}
                                <HStack spacing="3" wrap="wrap">
                                    <Button colorScheme="blue" size="sm" onClick={onOpen}>
                                        Add School
                                    </Button>
                                </HStack>
                            </Flex>

                            {/* Interactive search filters — Name, Code, Status. */}
                            <SimpleGrid columns={{base: 1, md: 3}} spacing="12px" width="100%">
                                <FormControl>
                                    <FormLabel fontSize="xs" color="gray.500" mb="4px">Name</FormLabel>
                                    <InputGroup size="sm">
                                        <InputLeftElement pointerEvents="none" children={<FiSearch color="gray"/>}/>
                                        <Input
                                            name="name"
                                            placeholder="Search by name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            borderRadius="8px"
                                        />
                                    </InputGroup>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="gray.500" mb="4px">Code</FormLabel>
                                    <InputGroup size="sm">
                                        <InputLeftElement pointerEvents="none" children={<FiSearch color="gray"/>}/>
                                        <Input
                                            name="code"
                                            placeholder="Search by code"
                                            value={filters.code}
                                            onChange={handleFilterChange}
                                            borderRadius="8px"
                                        />
                                    </InputGroup>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="gray.500" mb="4px">Status</FormLabel>
                                    <Select
                                        name="status"
                                        size="sm"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        placeholder="All statuses"
                                        borderRadius="8px"
                                    >
                                        <option value="Y">Active</option>
                                        <option value="N">Inactive</option>
                                        <option value="B">Blocked</option>
                                    </Select>
                                </FormControl>
                            </SimpleGrid>

                            <Flex justify="flex-end">
                                <Button size="xs" variant="ghost" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            </Flex>
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <Table variant='simple' color={textColor} size='sm' width='100%' style={{tableLayout: "auto"}}>
                            <Thead>
                                <Tr my='.8rem' pl='0px' color='gray.400'>
                                    {captions.map((caption, idx) => {
                                        return (
                                            <Th
                                                color='gray.400'
                                                key={idx}
                                                ps={idx === 0 ? "0px" : null}
                                                fontSize='10px'
                                                px='8px'
                                            >
                                                {caption}
                                            </Th>
                                        );
                                    })}
                                </Tr>
                            </Thead>
                            <Tbody>
                                <>
                                    {isOpen ?
                                        <AddSchoolModal
                                            isOpen={isOpen}
                                            onClose={onClose}
                                            handeSchoolUpload={handeSchoolUpload}
                                            handleSchoolSubmit={handleSchoolSubmit}
                                        /> : ""
                                    }
                                    {/* Edit modal — only mount once the detail is loaded so the
                                        form seeds from the fetched values. */}
                                    {isEditOpen && editSchoolData ?
                                        <AddSchoolModal
                                            isOpen={isEditOpen}
                                            onClose={closeEditSchool}
                                            handeSchoolUpload={handeSchoolUpload}
                                            handleSchoolSubmit={handleSchoolUpdate}
                                            isEdit={true}
                                            initialData={editSchoolData}
                                        /> : ""
                                    }
                                    {/* Brief loading state while the detail API is in flight. */}
                                    {isEditOpen && isLoadingEdit && !editSchoolData ?
                                        <Modal isOpen={true} onClose={closeEditSchool} isCentered>
                                            <ModalOverlay/>
                                            <ModalContent>
                                                <ModalBody>
                                                    <Flex justify="center" align="center" gap="12px" py="30px">
                                                        <Spinner color="blue.400" size="md"/>
                                                        <Text color="gray.500" fontSize="sm">Loading school
                                                            details...</Text>
                                                    </Flex>
                                                </ModalBody>
                                            </ModalContent>
                                        </Modal> : ""
                                    }
                                    {isLoadingSchools && (
                                        <Tr>
                                            <Td colSpan={captions.length} py="40px">
                                                <Flex justify="center" align="center" gap="12px">
                                                    <Spinner color="blue.400" size="md"/>
                                                    <Text color="gray.500" fontSize="sm">Loading schools...</Text>
                                                </Flex>
                                            </Td>
                                        </Tr>
                                    )}
                                    {!isLoadingSchools && schoolList && schoolList.map((school, index) => {
                                        // Serial number is continuous across pages.
                                        const serial = (page - 1) * size + index + 1;
                                        return (
                                            <SchoolTableRow
                                                key={school?.id ?? serial}
                                                serial={serial}
                                                school={school}
                                                setShowSchoolDetail={setShowSchoolDetail}
                                                onEditSchool={openEditSchool}
                                            />
                                        );
                                    })}
                                    {!isLoadingSchools && schoolList && schoolList.length === 0 && (
                                        <Tr>
                                            <Td colSpan={captions.length} py="40px" textAlign="center">
                                                <Text color="gray.500" fontSize="sm">No schools found.</Text>
                                            </Td>
                                        </Tr>
                                    )}
                                </>
                            </Tbody>
                            {/* Pagination lives in the table footer so it stays anchored
                                to the bottom of the datatable and spans every column. */}
                            <Tfoot>
                                <Tr>
                                    <Td colSpan={captions.length} px="0px" pt="16px" borderTopWidth="1px">
                                        <Flex
                                            id="school-table-pagination"
                                            justify="space-between"
                                            align="center"
                                            width="100%"
                                            gap="12px"
                                            wrap="wrap"
                                        >
                                            <HStack spacing="12px">
                                                <Text color="gray.500" fontSize="sm">
                                                    Page {page} of {totalPages} · {totalItems} schools
                                                </Text>
                                                <Select
                                                    size="sm"
                                                    width="auto"
                                                    value={size}
                                                    onChange={(event) => {
                                                        setPage(1);
                                                        setSize(Number(event.target.value));
                                                    }}
                                                >
                                                    {[10, 20, 50, 100].map((value) => (
                                                        <option key={value} value={value}>{value} / page</option>
                                                    ))}
                                                </Select>
                                            </HStack>
                                            <HStack spacing="8px" justify="flex-end" wrap="wrap">
                                                <Button
                                                    size="sm"
                                                    leftIcon={<FiChevronLeft/>}
                                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                                    isDisabled={isLoadingSchools || page <= 1}
                                                >
                                                    Previous
                                                </Button>

                                                {/* Numbered page selection with collapsing ellipses. */}
                                                {buildPageItems(page, totalPages).map((item) =>
                                                    typeof item === "number" ? (
                                                        <Button
                                                            key={item}
                                                            size="sm"
                                                            variant={item === page ? "solid" : "outline"}
                                                            colorScheme={item === page ? "blue" : "gray"}
                                                            onClick={() => setPage(item)}
                                                            isDisabled={isLoadingSchools}
                                                        >
                                                            {item}
                                                        </Button>
                                                    ) : (
                                                        <Text key={item} color="gray.400" px="4px" userSelect="none">
                                                            …
                                                        </Text>
                                                    )
                                                )}

                                                <Button
                                                    size="sm"
                                                    rightIcon={<FiChevronRight/>}
                                                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                                                    isDisabled={isLoadingSchools || page >= totalPages}
                                                >
                                                    Next
                                                </Button>
                                            </HStack>
                                        </Flex>
                                    </Td>
                                </Tr>
                            </Tfoot>
                        </Table>
                    </CardBody>
                </>
            }
        </Card>
    );
};

export default School;
